'use client';
import {
	EventIdentifier,
	FeatureTeamEvent,
	isPwaMode,
	TrackingEventType,
	useAnalytics,
	useAppTheme,
	useSettings
} from '@capsicum/common';
import { useOnboardingAnalytics } from '@capsicum/socios-core';
import { type Welcome } from '@capsicum/socios-core-web';
import { useSetAtom } from 'jotai';
import { type ComponentProps, useCallback, useEffect, useMemo, useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { Swiper, type SwiperClass, type SwiperProps, SwiperSlide } from 'swiper/react';

import { onboardingDoneAtom } from '../../data-access';

import { FirstPage } from './first-page/FirstPage';
import { FourthPage } from './FourthPage';
import { FourthPoblanoPage } from './FourthPoblanoPage';
import { SecondPage } from './SecondPage';
import { SecondPoblanoPage } from './SecondPoblanoPage';
import { ThirdPage } from './ThirdPage';
import { ThirdPoblanoPage } from './ThirdPoblanoPage';

type AnalyticsPayload = { page?: string; text: string };
type NavigationParams = AnalyticsPayload & { variant: ComponentProps<typeof Welcome>['variant'] };

export const Onboarding = () => {
	const [initWebApp, setInitWebApp] = useState(false);
	const [swiper, setSwiper] = useState<null | SwiperClass>();
	const setOnboardingDone = useSetAtom(onboardingDoneAtom);

	const analytics = useAnalytics();
	const { sendWalkthroughFinishEvent } = useOnboardingAnalytics();
	const { allThemes } = useAppTheme();

	const {
		settings: { isPoblanoEnabled, isRegistrationByEmailEnabled }
	} = useSettings();

	const goToLogin = useCallback(
		({ variant, ...otherParams }: NavigationParams) => {
			setOnboardingDone(true);
			analytics.sendEvent(TrackingEventType.track, EventIdentifier.ButtonClicked, {
				...otherParams,
				feature_team: FeatureTeamEvent.OFT
			});

			//Send a GA analytic event when a new user finishes the walkthrough
			variant === 'register' && sendWalkthroughFinishEvent();

			window.location.href = !isRegistrationByEmailEnabled || variant === 'login' ? '/login' : '/register';
		},
		[analytics, isRegistrationByEmailEnabled, sendWalkthroughFinishEvent]
	);

	const goToPrevPage = useCallback(() => {
		swiper?.slidePrev();
	}, [swiper]);

	const goToNextPage = useCallback(
		(params: AnalyticsPayload) => {
			if (!swiper) return;

			analytics.sendEvent(TrackingEventType.track, EventIdentifier.ButtonClicked, {
				...params,
				feature_team: FeatureTeamEvent.OFT
			});

			swiper.slideNext();
		},
		[swiper, analytics]
	);

	const onSlideChange = useCallback<Exclude<SwiperProps['onSlideChange'], undefined>>(swiper => {
		swiper.allowTouchMove = !swiper.isBeginning;
	}, []);

	const firstPageSlide = useMemo(() => {
		return (
			<FirstPage
				goToLogin={() => {
					goToLogin({ text: 'Login', variant: 'login' });
					analytics.sendEvent(TrackingEventType.track, EventIdentifier.LoginStart, {
						feature_team: FeatureTeamEvent.OFT
					});
				}}
				next={() => {
					goToNextPage({ text: 'Get Started' });
					analytics.sendEvent(TrackingEventType.track, EventIdentifier.SignUpStart, {
						feature_team: FeatureTeamEvent.OFT
					});
				}}
			/>
		);
	}, [goToLogin, goToNextPage, analytics]);

	const DEFAULT_SLIDES = useMemo(
		() => [
			firstPageSlide,
			<SecondPage
				goBack={goToPrevPage}
				key="second"
				next={() => goToNextPage({ page: 'onboarding_step1', text: 'Next' })}
			/>,
			<ThirdPage
				goBack={goToPrevPage}
				key="third"
				next={() => goToNextPage({ page: 'onboarding_step2', text: 'Next' })}
			/>,
			<FourthPage
				goBack={goToPrevPage}
				key="fourth"
				next={() => goToLogin({ page: 'onboarding_step3', text: 'Next', variant: 'register' })}
			/>
		],
		[firstPageSlide, goToPrevPage, goToNextPage, goToLogin]
	);

	const POBLANO_SLIDES = useMemo(
		() => [
			firstPageSlide,
			<SecondPoblanoPage
				goBack={goToPrevPage}
				key="second"
				next={() => goToNextPage({ page: 'onboarding_step1', text: 'Next' })}
			/>,
			<ThirdPoblanoPage
				goBack={goToPrevPage}
				key="third"
				next={() => goToNextPage({ page: 'onboarding_step2', text: 'Next' })}
			/>,
			<FourthPoblanoPage
				goBack={goToPrevPage}
				key="fourth"
				next={() => goToLogin({ page: 'onboarding_step3', text: 'Next', variant: 'register' })}
			/>
		],
		[firstPageSlide, goToPrevPage, goToNextPage, goToLogin]
	);

	const slides = useMemo(
		() => (isPoblanoEnabled ? POBLANO_SLIDES : DEFAULT_SLIDES),
		[isPoblanoEnabled, DEFAULT_SLIDES, POBLANO_SLIDES]
	);

	// useEffects
	useEffect(() => {
		// If the user has already seen the onboarding, don't resend the Segment Event
		if (!initWebApp) {
			setInitWebApp(true);
			analytics.sendEvent(TrackingEventType.track, EventIdentifier.OnboardingFirstWebOpen, {
				feature_team: FeatureTeamEvent.OFT,
				is_pwa_mode: isPwaMode()
			});
		}
	}, [initWebApp, analytics]);

	return (
		<ThemeProvider theme={allThemes.dark}>
			<S.StyledSwiper
				allowTouchMove={false}
				lazyPreloadPrevNext={3}
				slidesPerView={1}
				onSlideChange={onSlideChange}
				onSwiper={setSwiper}
			>
				{slides.map((slide, index) => (
					<SwiperSlide key={`slider_page_${index}`}>{slide}</SwiperSlide>
				))}
			</S.StyledSwiper>
		</ThemeProvider>
	);
};

namespace S {
	export const StyledSwiper = styled(Swiper)`
		height: 100%;
		background-color: ${({ theme }) => theme.colors.decorativeMarketing};
	`;
}
